<template>
  <div
    ref="container"
    class="flex flex-col justify-center items-center overflow-hidden bg-no-repeat bg-contain bg-center bg-slate-900 relative video-container in-call-wrapper text-gray-800 max-w-full max-h-full"
    :class="{ windowed: $props.isWindowed }"
    :style="{
      backgroundImage: `url(${backgroundImage})`
    }"
  >
    <div class="overlay interrupted" v-if="meeting.isAudioInterrupted">	
      <div class="overlay-inner pa-4 ma-auto">
          <div class="overlay-text">Your session was interrupted. Click the Resume button to continue.</div>
          <div class="overlay-actions d-flex justify-end">
              <button
                  class="pa-4"
                  @click="resume">Resume</button>
          </div>
        </div>
    </div>
    <div class="overlay user-gesture-required" v-if="userGestureRequired">	
      <div class="overlay-inner pa-4 ma-auto">
          <div class="overlay-text">When you are ready, click "Join" to start your call.</div>
          <div class="overlay-actions d-flex justify-end">
              <button
                  class="pa-4"
                  @click="join">Join</button>
          </div>
        </div>
    </div>
    <div class="overlay closed" v-if="meeting.isClosed && meeting.closedReasonMessage">
      <div class="overlay-inner pa-4 ma-auto">
          <div class="overlay-text">{{meeting.closedReasonMessage}}</div>
        </div>
    </div>
    <div class="flex gap-1 flex-col justify-start items-start top-1 left-1 absolute" style="z-index: 2;">
      
      <!-- User Connecting -->
      <div class="w-full flex gap-1" v-if="meeting.isReady || meeting.isWaiting">
        <div
          class="size-3 rounded-full animate-pulse"
          :class="{
            'bg-green-500': connected,
            'bg-yellow-600': !connected,
          }"
        ></div>
        <div
          class="text-xs text-truncate"
          :class="{
            'text-green-500': connected,
            'text-yellow-600': !connected
          }"
        >
          {{ userName }} (<template v-if="connected">Connected</template>
          <template v-else-if="meeting.isConnecting">Connecting...</template>
          <template v-else>Reconnecting... </template>)
        </div>
      </div>
      <!-- /User Connecting -->
      <!-- Agent Connecting -->
      <div class="w-full flex flex-col gap-1" v-if="meeting">
        <div class="flex gap-1">
          <div
            class="size-3 rounded-full animate-pulse"
            :class="{
              'bg-green-500': (meeting.participants.length ?? 0) >= 2,
              'bg-yellow-500': (meeting.participants.length  ?? 0) < 2
            }"
          ></div>
          <div
            class="text-xs text-truncate"
            :class="{
              'text-green-500': (meeting.participants.length  ?? 0) >= 2,
              'text-yellow-500': (meeting.participants.length  ?? 0) < 2
            }"
          >
            {{ agentName }} (<template v-if="(meeting.participants.length ?? 0) >= 2">Connected</template>
            <template v-else-if="agentHasConnected">Waiting for the agent to reconnect.</template>
            <template v-else>Connecting...</template>)
          </div>
        </div>
      </div>
      <!-- / Agent Connecting -->
      <!-- Recording Status -->
      <div v-if="meeting.isRecording" class="mt-2 bg-red-500 rounded" style="height:28px;line-height:28px;padding:0 2px" @click='resetHideRecordingText()'>
        <div class="animate-pulse flex text-white">
          <i style="font-size:24px" class="icon-recording-circle"></i>
          <span style="font-size:16px; margin:auto" v-if="!hideRecordingText" :class="{ 'fade-out': hideRecordingText }" class="text-xs ml-1">Recording</span>
        </div>
      </div>
      <!-- Recording Status -->
    </div>
    <template v-for="media in remoteUserMedia" :key="media.id">
      <div class="flex justify-center align-middle remote-video w-32 h-24" :class="remoteVideoCSS" style="z-index: 1;">
        <!--<video
          class="w-full h-full object-contain"
          webkit-playsinline
          @loadedmetadata="playViaEvent"
          @loadstart="playViaEvent"
          :ref="async (el: any) => await runVideoPlay(el)"
          v-if="media.videoTrack.stream"
          v-show="media && media.videoTrack && !media.videoTrack.isMuted"
          playsinline
          :srcObject.prop.camel="media.stream"
        ></video>-->
        <video-player-container>
          <div class="remote-user-media" v-participant-element="media" v-if="media" :data-logo-url="settings.logoUrl" ></div>
        </video-player-container>
      </div>
    </template>
    <template v-for="media in remoteDisplayMedia" :key="media.id">
      <!--<video
        class="remote-video max-w-full max-h-full screen liveswitch-remote-screenshare h-full"
        :ref="async (el: any) => await runVideoPlay(el)"
        @loadedmetadata="playViaEvent"
        @loadstart="playViaEvent"
        playsinline
        webkit-playsinline
        :srcObject.prop.camel="media.stream"
        @dblclick="fullscreenScreenShare"
      ></video>-->
      <video-player-container>
        <div class="remote-display-media" v-participant-element="media" v-if="media" :data-logo-url="settings.logoUrl"></div>
      </video-player-container>
    </template>
    <div
      class="absolute bg-transparent opacity-20 pointer-events-none top-0 left-0 right-0 bottom-0 justify-center items-center hover-remote-screenshare hidden text-white flex-col gap-2 text-2xl"
    >
      <i class="icon-fullscreen text-4xl"></i>
      <div><span>Double click for full screen.</span></div>
    </div>
    <div class="absolute top-1 right-1 flex flex-col justify-start gap-2 align-middle" :class="localVideoCSS" style="margin-top:1px; z-index: 1;">
      <!--<video
        class="object-contain liveswitch-local-webcam-video w-full h-full"
        :class="{
          'scale-x-[-1]': false //TODO: mirroring for local
        }"
        webkit-playsinline
        muted="true"
        @loadedmetadata="playViaEvent"
        @loadstart="playViaEvent"
        :ref="async (el: any) => await runVideoPlay(el)"
        v-if="liveSwitchMeeting.userMedia.videoTrack.stream"
        v-show="
          liveSwitchMeeting.userMedia &&
          liveSwitchMeeting.userMedia.videoTrack &&
          !liveSwitchMeeting.userMedia.videoTrack.isMuted
        "
        playsinline
        :srcObject.prop.camel="liveSwitchMeeting.userMedia.stream"
      ></video>-->
      <video-player-container>
        <div class="local-user-media" v-participant-element="meeting.me.element || null" v-if="meeting.me && meeting.isCameraMuted === false" :data-logo-url="settings.logoUrl"></div>
      </video-player-container>
      <!--<video
        class="local-video max-w-32 max-h-24 object-contain"
        muted="true"
        v-if="
          liveSwitchMeeting.displayMedia.videoTrack.stream &&
          liveSwitchMeeting.displayMedia.videoTrack.isStarted &&
          !liveSwitchMeeting.displayMedia.videoTrack.isMuted
        "
        :ref="async (el: any) => await runVideoPlay(el)"
        @loadedmetadata="playViaEvent"
        @loadstart="playViaEvent"
        playsinline
        webkit-playsinline
        :srcObject.prop.camel="liveSwitchMeeting.displayMedia.stream"
      ></video>-->
      <video-player-container>
        <div class="local-display-media" v-participant-element="meeting.screenShareElement || null" v-if="meeting.screenShareElement && meeting.iAmScreenSharing" :data-logo-url="settings.logoUrl"></div>
      </video-player-container>
    </div>
    <div
      class="absolute bottom-1 flex gap-4 bg-gray-400/30 opacity-30 hover:opacity-90 p-3 rounded-lg left-1/2 -translate-x-1/2 w-auto text-center z-10"
    >
      <div class="group relative" v-if="meeting.isMediaReady">
        <button
          class="size-12 text-white p-2 rounded-full focus:outline-none transition duration-300 text-2xl flex flex-col justify-center items-center"
          :class="{
            'bg-green-600 hover:bg-green-500': isSendingVideo,
            'bg-red-600  hover:bg-red-500': !isSendingVideo
          }"
          type="button"
          @click="meeting.isCameraMuted = !meeting.isCameraMuted"
        >
          <i
            :class="{
              'icon-video-slash-solid': !isSendingVideo,
              'icon-video-solid': isSendingVideo
            }"
          ></i>
        </button>
        <div
          class="absolute hidden group-hover:block bg-gray-800 text-white text-sm py-2 px-4 rounded shadow-lg bottom-full mb-2 -left-3 whitespace-nowrap"
        >
          {{ isSendingVideo ? 'Stop Sharing Webcam' : 'Start Sharing Webcam' }}
        </div>
      </div>
      <div class="group relative" v-if="meeting.isMediaReady">
        <button
          class="size-12 text-white p-2 rounded-full focus:outline-none transition duration-300 text-2xl flex flex-col justify-center items-center"
          :class="{
            'bg-green-600 hover:bg-green-500': isSendingAudio,
            'bg-red-600  hover:bg-red-500': !isSendingAudio
          }"
          type="button"
          @click="meeting.isMicrophoneMuted = !meeting.isMicrophoneMuted"
        >
          <i
            :class="{
              'icon-audio-mic-slash-solid': !isSendingAudio,
              'icon-audio-mic-solid': isSendingAudio
            }"
          ></i>
        </button>
        <div
          class="absolute hidden group-hover:block bg-gray-800 text-white text-sm py-2 px-4 rounded shadow-lg bottom-full mb-2 left-1/2 transform -translate-x-1/2 whitespace-nowrap"
        >
          {{ isSendingAudio ? 'Stop Sharing Microphone' : 'Start Sharing Microphone' }}
        </div>
      </div>
      <div
        class="group relative"
        v-if="
          meeting.isScreenShareSupported && canShareScreen() && props.sourceType !== SourceType.KIOSK
        "
      >
        <button
          class="size-12 text-white p-2 rounded-full focus:outline-none transition duration-300 text-2xl flex flex-col justify-center items-center"
          :class="{
            'bg-green-600 hover:bg-green-500': isSendingScreenShare,
            'bg-red-600  hover:bg-red-500': !isSendingScreenShare
          }"
          type="button"
          @click="meeting.iAmScreenSharing = !meeting.iAmScreenSharing"
        >
          <i
            :class="{
              'icon-screenshare-slash': !isSendingScreenShare,
              'icon-screenshare': isSendingScreenShare
            }"
          ></i>
        </button>
        <div
          class="absolute hidden group-hover:block bg-gray-800 text-white text-sm py-2 px-4 rounded shadow-lg bottom-full mb-2 left-1/2 transform -translate-x-1/2 whitespace-nowrap"
        >
          {{ isSendingScreenShare ? 'Stop Sharing Screen' : 'Start Sharing Screen' }}
        </div>
      </div>
      <div class="group relative" v-if="meeting.isMediaReady">
        <button
          class="size-12 bg-yellow-600 hover:bg-yellow-500 text-white p-2 rounded-full focus:outline-none transition ease-in-out text-2xl flex flex-col justify-center items-center"
          type="button"
          @click="setShowSettingsArea(true)"
        >
          <i class="icon-cog-gear"></i>
        </button>
        <div
          class="absolute hidden group-hover:block bg-gray-800 text-white text-sm py-2 px-4 rounded shadow-lg bottom-full mb-2 left-1/2 transform -translate-x-1/2 whitespace-nowrap"
        >
          Settings
        </div>
      </div>
      <div class="group relative">
        <button
          class="size-12 bg-red-600 hover:bg-red-500 text-white p-2 rounded-full focus:outline-none transition ease-in-out text-2xl flex flex-col justify-center items-center"
          type="button"
          @click="close"
        >
          <i class="icon-close-large"></i>
        </button>
        <div
          class="absolute hidden group-hover:block bg-gray-800 text-white text-sm py-2 px-4 rounded shadow-lg bottom-full mb-2 left-1/2 transform -translate-x-1/2 whitespace-nowrap"
        >
          End Call
        </div>
      </div>
    </div>
    <div
      v-if="showSettingsArea"
      class="absolute z-50 w-full h-full bg-black/30 flex justify-center items-center flex-col p-2"
    >
      <!-- The actual dialog panel -->
      <div
        class="relative transform overflow-auto rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6"
      >
        <i
          class="absolute top-2 right-2 icon-close-large cursor-pointer"
          @click="setShowSettingsArea(false)"
        ></i>

        <!-- Video Settings -->
        <template v-if="meeting.isMediaReady">
          <p class="text-sm font-semibold leading-6 text-gray-900 mb-2">Video Devices</p>
          <div class="grid grid-cols-[auto,1fr,auto] gap-2 mb-4 text-sm" role="list">
            <template v-for="device in videoInputs">
              <div
                class="flex items-center justify-center group"
                :class="{
                  selected:
                    meeting.activeCamera === device.deviceId
                }"
              >
                <i class="icon-video-solid group-[.selected]:text-blue-400"></i>
              </div>
              <div
                class="cursor-pointer group"
                @click="switchDeviceByTypeAndId(DeviceType.VideoInput, device.deviceId)"
                @touchstart="switchDeviceByTypeAndId(DeviceType.VideoInput, device.deviceId)"
                :class="{
                  selected:
                    meeting.activeCamera === device.deviceId
                }"
              >
                <span class="group-[.selected]:text-blue-400">{{ device.label }}</span>
              </div>
              <div class="flex items-center justify-center">
                <i
                  v-if="
                    meeting.activeCamera === device.deviceId
                  "
                  class="icon-check-circle text-blue-400"
                ></i>
              </div>
            </template>
          </div>
        </template>

        <!-- Audio Settings -->
        <template v-if="meeting.isMediaReady">
          <p class="text-sm font-semibold leading-6 text-gray-900 mb-2">Microphone Devices</p>
          <div class="grid grid-cols-[auto,1fr,auto] gap-2 text-sm" role="list">
            <template v-for="device in audioInputs">
              <div
                class="flex items-center justify-center group"
                :class="{
                  selected: meeting.activeMicrophone === device.deviceId
                }"
              >
                <i class="icon-audio-mic-solid group-[.selected]:text-blue-400"></i>
              </div>
              <div
                class="cursor-pointer group"
                @click="switchDeviceByTypeAndId(DeviceType.AudioInput, device.deviceId)"
                @touchstart="switchDeviceByTypeAndId(DeviceType.AudioInput, device.deviceId)"
                :class="{
                  selected:meeting.activeMicrophone === device.deviceId
                }"
              >
                <span class="group-[.selected]:text-blue-400">{{ device.label }}</span>
              </div>
              <div class="flex items-center justify-center">
                <i
                  v-if="meeting.activeMicrophone === device.deviceId
                  "
                  class="icon-check-circle text-blue-400"
                ></i>
              </div>
            </template>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import type { UserDetails } from 'types/UserDetails'
import type { BubbleConfig } from 'types/BubbleConfig'
import type { Conversation } from 'types/Conversation'
import {
  type Ref,
  ref,
  onBeforeMount,
  type ComputedRef,
  computed,
  onMounted,
  onBeforeUnmount,
  onErrorCaptured,
  reactive,
  type Reactive,
  watch
} from 'vue'
import { Media, type Meeting, MediaDeviceManager, type MeetingAttendeeEvent } from '@liveswitch/sdk'
import { SourceType } from '@/utility/Constants'
import defaultLogo from '@/assets/default-logo.webp'
import { UAParser } from 'ua-parser-js'
import { Session } from '@/scripts/Session'
import type { LastActiveCall } from 'types/LastActiveCall'
import { API } from '@/utility/Api'
import type { Organization } from 'types/Organization'
import { captureException } from '@/bubble'
import { useMeetings } from '@meeting/composables/useMeetings'
import { useMeetingConfiguration } from '@meeting/composables/useMeetingConfiguration'
import vParticipantElement from '@meeting/components/vParticipantElement'
import { BeaconMediaClient } from '@/utility/BeaconMediaClient'
import type { MediaDevice } from '@meeting/types/MediaDevice'
import { useMeetingClasses } from '@meeting/composables/useMeetingClasses'
import { media } from '@meeting/helpers/media'

let container: Ref<any> = ref()
const agentHasConnected: Ref<boolean> = ref(false)
const isMobile = () => {
  const { device } = UAParser(navigator.userAgent)
  return !!device.type // Desktops never have a type.
}
const backgroundImage = ref(defaultLogo);
const hideRecordingText = ref(false);

const canShareScreen = () => {
  return meeting.value.isScreenShareSupported
}

const settings = {
  logoUrl: defaultLogo
}

const props = defineProps<{
  config: BubbleConfig
  userDetails?: UserDetails
  conversation: Conversation
  isWindowed: boolean
  sourceType: SourceType
}>()

Session.setSession(props as LastActiveCall)

const emits = defineEmits<{
  (e: 'save', user: UserDetails): UserDetails
  (e: 'close'): void
}>()

/*const liveSwitchMeeting: Reactive<ZoomMeeting> = reactive(new ZoomMeeting(
  props.conversation,
  props.sourceType === SourceType.KIOSK || props.sourceType === SourceType.QRCODE,
  props.userDetails
))*/


enum DeviceType { 
  VideoInput,
  AudioInput,
  AudioOutput
}

const showSettingsArea: Ref<boolean> = ref(false)
const setShowSettingsArea = (value : boolean) : void => {
  showSettingsArea.value = value
}

const audioInputs: ComputedRef<MediaDevice[]> = computed(() => {
  return meeting.value.microphones
})
const videoInputs: ComputedRef<MediaDevice[]> = computed(() => {
  return meeting.value.cameras
})

const meetingsStore = useMeetings()
const meeting = computed(() => meetingsStore.getMeeting().value)

const connected = computed(() => {
  return (meeting.value.isReady || meeting.value.isWaiting) && !meeting.value.networkLost
})

const userGestureRequired: Ref<boolean> = ref(false)

onMounted(async () => {


  const api = new API()
  const resp = await api.getAuth(`/public/organizations/${props.conversation.organizationId}`, {});
  const org: Organization = resp.data as Organization;
  if (org && org.logoUrl) { 
    backgroundImage.value = org.logoUrl;
    settings.logoUrl = org.logoUrl;
  }

  useMeetingClasses(container.value)

  watch(() => meeting.value.isClosed, (newVal) => {
    if (newVal) {
      console.log('Meeting is closed, clearing meeting store')
      close()
    }
  })

  
  // create a meeting token
  const meetingTokenResponse = await new API().createToken({
      conversationId: props.conversation.id,
      isOwner: false,
      username: `${props.userDetails?.firstName ?? 'Visitor'} ${props.userDetails?.lastName ?? ''}`,
      provider: 'zoom',
  })
  if(!meetingTokenResponse || !meetingTokenResponse.data || !meetingTokenResponse.data.apiKey) {
    throw new Error('Failed to get meeting token')
  }

  const defaultCameraOff = props.sourceType === SourceType.WEBSITE

  // state from a prior call
  const currentCallState = Session.getCallState(props.conversation.id) || {
      audioMuted: false,
      videoMuted: defaultCameraOff,
      screenShare: false,
      conversationId: props.conversation.id
  }

  // general configuration
  const meetingConfiguration = useMeetingConfiguration()
  meetingConfiguration.getSettings().token = meetingTokenResponse.data.apiKey
  // kiosk/qrcode require camera, and have it on by default
  meetingConfiguration.getSettings().defaultCameraOff = currentCallState.videoMuted
  meetingConfiguration.getSettings().defaultMicrophoneOff = currentCallState.audioMuted
  // todo: add screen share state
  meetingConfiguration.getSettings().isCameraRequired = props.sourceType !== SourceType.WEBSITE
  
  // create a media client
  meeting.value.mediaClient = new BeaconMediaClient()

  
  // track muted state and save it
  watch(() => [meeting.value.isCameraMuted, meeting.value.isMicrophoneMuted], (newVal) => {
    if (meeting.value.isCameraMuted === true || meeting.value.isCameraMuted === false) {
      currentCallState.videoMuted = meeting.value.isCameraMuted
    }
    if (meeting.value.isMicrophoneMuted === true || meeting.value.isMicrophoneMuted === false) {
      currentCallState.audioMuted = meeting.value.isMicrophoneMuted
    }
    Session.setCallState(currentCallState)
    console.log('Call state updated:', currentCallState)
  })

  if(media.isUserGestureRequired()){
    console.log('User gesture required')
    userGestureRequired.value = true
  }else{
    console.log('User gesture not required, joining')
    meetingsStore.join()
  }

  watch(() => meeting.value.participants, () =>{
    console.log('Participants changed:', meeting.value.participants.length)
      if (meeting.value.participants.filter((p) => p.isHost || p.isManager).length > 0) {
        agentHasConnected.value = true
        console.log('Agent has connected')
      }
  }, { deep: true })
})

function resetHideRecordingText() {
			hideRecordingText.value = false;
			setTimeout(() => {
				hideRecordingText.value = true;
			}, 2500);
		}

watch(
  () => meeting.value.isRecording,
  (newVal) => {
    if (newVal === true) {
      resetHideRecordingText()
    } else {
      hideRecordingText.value = false;
    }
  }
);

onBeforeMount(async () => {
  try {
    /*if (!MediaDeviceManager.shared.isStarted && !MediaDeviceManager.shared.isStarting) {
      await MediaDeviceManager.shared.start()
    }*/
    /* 
      Attempts to detect if the currently in use device was unplugged. If so it will switch to another device or mute the track if no other device exists.
    */
    const tryAndSwitchDeviceAfterUnplug = async (type: "video" | "audio", e : any) : Promise<void> => {
      const currentDeviceId = type === "video" ? meeting.value.activeCamera : meeting.value.activeMicrophone;
      if (!currentDeviceId) {
        return;
      }
      for (let i = 0, l = e.removed.length; i < l; i++) {
        const device = e.removed[i];
        if (device && currentDeviceId === device.id) {
          if (type === "video") { 
            if (e.manager.videoInputs.length > 0) {
              try {
                const newVideoInput = e.manager.videoInputs[0];
                //await liveSwitchMeeting?.meeting?.localUserMedia?.setVideoDevice(newVideoInput.id, true);
                meeting.value.activeCamera = newVideoInput.id;
              } catch (e) {
                captureException(e);
              }
            } else {
              //await liveSwitchMeeting?.toggleVideoMuted();
              meeting.value.isCameraMuted = !meeting.value.isCameraMuted
            }
          } else {
            if (e.manager.audioInputs.length > 0) {
              try {
                const newAudioInput = e.manager.audioInputs[0];
                //await liveSwitchMeeting?.meeting?.localUserMedia?.setAudioDevice(newAudioInput.id, true);
                meeting.value.activeMicrophone = newAudioInput.id;
              } catch (e) {
                captureException(e);
              }
            } else {
              //await liveSwitchMeeting?.toggleAudioMuted();
              meeting.value.isMicrophoneMuted = !meeting.value.isMicrophoneMuted
            }
          }
        }
      }
    }
    /*MediaDeviceManager.shared.videoInputsUpdated.bind(async (e) => { 
      await tryAndSwitchDeviceAfterUnplug("video", e);
    });
    MediaDeviceManager.shared.audioInputsUpdated.bind(async (e) => {
      await tryAndSwitchDeviceAfterUnplug("audio", e);
    });*/
  } catch (e) {
    captureException(e);
  }
})

const resume = () => {
    meeting.value.resumeAudioPlayback()
    meeting.value.resumeVideoPlayback()
}

const join = () => {
  userGestureRequired.value = false
  meetingsStore.join()
}

// // UI Computeds
const userName: ComputedRef<string> = computed<string>(() => {
  const name = []
  if (props.userDetails?.firstName) {
    name.push(props.userDetails.firstName)
  }
  if (props.userDetails?.lastName) {
    name.push(props.userDetails.lastName)
  }
  if (name.length === 0) {
    name.push('Visitor (You)')
  }
  return name.join(' ')
})
const agentName: ComputedRef<string> = computed<string>(() => {
  if (props.conversation && props.conversation.Users && props.conversation.Users.length > 0) {
    const name = []
    const user = props.conversation.Users[0]
    if (user.firstname) {
      name.push(user.firstname)
    }
    if (user.lastname) {
      name.push(user.lastname)
    }
    return name.join(' ')
  }
  return 'Agent'
})

const remoteDisplayMedia: ComputedRef<HTMLElement[]> = computed<HTMLElement[]>(() => {
  return (meeting.value.isAnyScreenShareActive && !meeting.value.iAmScreenSharing && meeting.value.screenShareElement) ? [meeting.value.screenShareElement] : []
  //return liveSwitchMeeting?.meeting?.visibleDisplayMedias.filter((x: Media) => x.isRemote) ?? []
})
const remoteUserMedia: ComputedRef<HTMLElement[]> = computed<HTMLElement[]>(() => {
  const remotes = meeting.value.participants.filter((x) => (x.isHost || x.isManager) && x.bVideoOn)
  if (remotes.length === 0) {
    return []
  }
  return remotes.map(x=>x.element).filter((el) => el != null) as HTMLElement[]
  //return liveSwitchMeeting?.meeting?.visibleUserMedias.filter((x: Media) => x.isRemote) ?? []
})
const isSendingAudio: ComputedRef<boolean> = computed<boolean>(() => {
  return !meeting.value.isMicrophoneMuted // !liveSwitchMeeting.userMedia.audioTrack.isMuted
})
const isSendingVideo: ComputedRef<boolean> = computed<boolean>(() => {
  return (
    !meeting.value.isCameraMuted && //!liveSwitchMeeting?.userMedia.videoTrack.isMuted &&
    meeting.value.isCameraAvailable//liveSwitchMeeting?.userMedia.videoTrack.isStarted
  )
})
const isSendingScreenShare: ComputedRef<boolean> = computed<boolean>(() => {
  return meeting.value.iAmScreenSharing ?? false // liveSwitchMeeting.displayMedia.videoTrack.isStarted
})

const remoteVideoCSS: ComputedRef<string> = computed<string>(() => { 
  //if (remoteUserMedia.value.length === 0 || remoteUserMedia.value[0].videoTrack.isMuted) {
  if (remoteUserMedia.value.length === 0) {
    return "";
  }
  if (remoteDisplayMedia.value.length === 0) { 
    return "max-w-full max-h-full h-full w-full bg-slate-900";
  }
  else {
    //if (!liveSwitchMeeting.userMedia || !liveSwitchMeeting.userMedia.videoTrack || liveSwitchMeeting.userMedia.videoTrack.isMuted) {
    if(meeting.value.isCameraMuted || !meeting.value.isCameraAvailable) {
      return "absolute right-1 max-h-24 top-1 max-w-32 bg-black"
    } else {
      return 'absolute right-1 max-h-24 top-28 max-w-32 bg-black';
    }
  }
});
const localVideoCSS: ComputedRef<string> = computed<string>(() => { 
  //if (!meeting.liveSwitchMeeting.userMedia || !liveSwitchMeeting.userMedia.videoTrack || liveSwitchMeeting.userMedia.videoTrack.isMuted) {
  if(meeting.value.isCameraMuted || !meeting.value.isCameraAvailable) {
    return "";
  }
  if (remoteDisplayMedia.value.length > 0) {
    return "bg-gray-500 w-32 h-24"
  }
  if (isSendingScreenShare) {
    return "w-32 h-32"; 
  }
  return "w-32 h-24";
})
const fullscreenScreenShare = (ev: MouseEvent) => {
  const video: HTMLVideoElement = ev.target as HTMLVideoElement
  if (
    !(
      (
        document.fullscreenElement /* Standard syntax */ ||
        (document as any).webkitFullscreenElement /* Safari and Opera syntax */ ||
        (document as any).msFullscreenElement
      ) /* IE11 syntax */
    ) &&
    video
  ) {
    if (video.requestFullscreen) {
      video.requestFullscreen()
    } else if ((video as any).webkitRequestFullscreen) {
      /* Safari */
      ;(video as any).webkitRequestFullscreen()
    } else if ((video as any).msRequestFullscreen) {
      /* IE11 */
      ;(video as any).msRequestFullscreen()
    }
  } else {
    if (document.exitFullscreen) {
      document.exitFullscreen()
    } else if ((document as any).webkitExitFullscreen) {
      /* Safari */
      ;(document as any).webkitExitFullscreen()
    } else if ((document as any).msExitFullscreen) {
      /* IE11 */
      ;(document as any).msExitFullscreen()
    }
  }
}
onBeforeUnmount(async () => {
  try {
    console.log('Unmounting InCall component')
    await meeting.value.destroy(false)
    meetingsStore.clearMeeting()
  } catch (e) {
    captureException(e);
  }
})
const close = async () => {
  try {
    console.log('Closing InCall component')
    await meeting.value.destroy(false)
    meetingsStore.clearMeeting()
  } catch (e) {
    captureException(e);
  }
  emits('close')
}
const runVideoPlay = async (el: HTMLVideoElement) => {
  if (el) {
    try {
      await el.play()
    } catch (e) {
      captureException(e);
    }
  }
}
const playViaEvent = async (ev: Event) => {
  try {
    if (ev && ev.target) {
      await (ev.target as HTMLVideoElement).play()
    }
  } catch (e) {
    captureException(e);
  }
}
const switchDeviceByTypeAndId = async (type: DeviceType, id: string) => {
  try {
    if (type === DeviceType.VideoInput) {
      meeting.value.activeCamera = id;// await liveSwitchMeeting?.meeting?.setVideoInputDevice(id)
    } else if (type === DeviceType.AudioInput) {
      //await liveSwitchMeeting?.meeting?.setAudioInputDevice(id)
      meeting.value.activeMicrophone = id;
    }
    showSettingsArea.value = false
  } catch (e) {
    captureException(e);
  }
}
onErrorCaptured((err, vm, info) => {
  captureException(err);
})
</script>
<style scoped>
@import url('/src/styles/icons.css');
@tailwind base;
@tailwind components;
@tailwind utilities;
@media (pointer: fine) {
  div.video-container > video.liveswitch-remote-screenshare:hover + div.hover-remote-screenshare {
    display: flex;
  }
}
div.video-container:has(video.remote-video:not([style*='display: none;'])) {
  background-size: 0;
}
div {
  font-family: 'Inter Var', 'ui-sans-serif', 'system-ui', 'sans-serif';
}
@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}
.fade-out {
  animation: fadeOut 1s forwards;
}
.text-truncate {
  white-space: nowrap;
}

</style>
<style>
@import '@meeting/assets/css/mirroring.css';

video-player-container {
  position: relative;
  width: 100%;
  height: 100%;
}
video-player{
  width: 100%;
  height: 100%;
}
.screenshare.other-screenshare{
  height:100%;
  width: 100%;
  object-fit: contain;
}
.screenshare.my-screenshare{
  height: 76px;
  width: 128px;
}
div.local-user-media{
  height:100%;
  width: 100%;
  min-height: 96px;
}
div.local-user-media video{
  object-fit: contain;
}
div.remote-user-media{
  height:100%;
  width: 100%;
  min-height: 96px;
}
div.remote-display-media{
  height:100%;
  width: 100%;
  background-color: #10172A;
  min-height: 120px;
}
.overlay{
  border-radius: 12px;
  padding: 12px;
  margin: 12px;
  background-color: #10172A;
  color:#fff;
  position: absolute;
  z-index: 2;
}
.overlay-inner{
  display: flex;
}
.overlay-inner .overlay-text{
  padding:12px;
}
.overlay-actions{
  color:white;
}
.overlay-actions button{
  height: 100%;
  border: 1px solid white;
  padding: 12px;
  border-radius: 12px;
}
</style>
